import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import { requestPasswordReset } from '../api/auth';
import { extractApiError } from '../utils/apiError';
import SocialLoginButtons from '../components/SocialLoginButtons';
import FloatingLanguageSwitcher from '../components/FloatingLanguageSwitcher';
import { EyeIcon, EyeSlashIcon } from '../components/icons/EyeIcons';
import { motion, AnimatePresence } from 'motion/react';
import { Mail, Lock, CheckCircle } from 'lucide-react';
import { Card, Button, TextField, FieldError } from '../components/ui';

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const login = useAuthStore((s) => s.login);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Forgot password state
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState('');
  const [forgotResetUrl, setForgotResetUrl] = useState('');
  const [forgotError, setForgotError] = useState('');

  const handleForgotSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotSuccess('');
    setForgotResetUrl('');
    setForgotLoading(true);
    try {
      const res = await requestPasswordReset(forgotEmail);
      setForgotSuccess(res.message);
      if (res.reset_url) setForgotResetUrl(res.reset_url);
    } catch (err: unknown) {
      setForgotError(extractApiError(err, t('auth.forgot.errorDefault')));
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      const u = useAuthStore.getState().user;
      // ?next= deep-link (e.g. /admin/...) wins; internal paths only.
      const next = searchParams.get('next');
      if (next && next.startsWith('/') && !next.startsWith('//')) {
        navigate(next);
      } else if (u?.staff_role && !u.onboarding_completed) {
        // Pure staff account (never onboarded as a learner) → admin console.
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (err: unknown) {
      const loginErr = err as { emailNotVerified?: boolean; email?: string; response?: { data?: { detail?: string } } };
      if (loginErr.emailNotVerified) {
        navigate('/verify-email', { state: { email: loginErr.email } });
        return;
      }
      setError(loginErr.response?.data?.detail ?? t('auth.login.errorDefault'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-50 dark:bg-canvas-dark">
      {/* Ambient glow — Variant B */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -top-32 left-1/4 h-[480px] w-[480px] rounded-full bg-blue-500/10 blur-[120px] dark:bg-blue-500/15" />
        <div className="absolute -top-12 right-1/4 h-[420px] w-[420px] rounded-full bg-blue-400/8 blur-[120px] dark:bg-blue-400/10" />
        <div className="absolute bottom-0 left-1/2 h-[280px] w-[280px] -translate-x-1/2 rounded-full bg-amber-400/8 blur-[100px] dark:bg-amber-400/10" />
      </div>

      <FloatingLanguageSwitcher />

      <div className="relative z-10 mx-auto flex w-full max-w-5xl flex-col items-center gap-12 px-6 py-16 md:flex-row md:gap-20">

        {/* Left branding panel */}
        <div className="hidden flex-1 space-y-7 md:block">
          <div className="mb-6 flex items-center gap-3">
            <svg width="44" height="44" viewBox="0 0 32 32" fill="none" aria-hidden="true">
              <rect x="0.5" y="0.5" width="31" height="31" rx="8" fill="#16213A" stroke="rgba(255,255,255,.1)" />
              <path d="M9 11.5C9 9.567 10.567 8 12.5 8H22V11H13C12.4477 11 12 11.4477 12 12V20C12 20.5523 12.4477 21 13 21H22V24H12.5C10.567 24 9 22.433 9 20.5V11.5Z" fill="#fff" />
              <path d="M19 13L16 18H19L17.5 24L23 16.5H20L22 13H19Z" fill="#F59E0B" />
            </svg>
            <span className="text-[26px] font-extrabold tracking-tight text-slate-900 dark:text-white">
              Cloud<span className="text-blue-600 dark:text-blue-400">Master</span>
            </span>
          </div>

          <h2 className="text-[44px] font-extrabold leading-[1.05] tracking-[-0.03em] text-slate-900 dark:text-white lg:text-[54px]">
            {t('auth.brand.joinHeadline')}<br />
            <span className="text-blue-600 dark:text-blue-400">
              {t('auth.brand.joinCount')}
            </span><br />
            {t('auth.brand.joinSubtitle')}
          </h2>

          <div className="flex gap-3 pt-4">
            {[
              { label: 'AWS', g: 'from-amber-500 to-amber-600' },
              { label: 'AZ', g: 'from-blue-500 to-blue-700' },
              { label: 'GCP', g: 'from-teal-500 to-green-600' },
            ].map(({ label, g }) => (
              <span
                key={label}
                className={`flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br ${g} text-[11px] font-extrabold text-white shadow-md`}
              >
                {label}
              </span>
            ))}
          </div>
        </div>

        {/* Auth card */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <Card className="p-7 sm:p-8">
            <AnimatePresence mode="wait">
              {forgotMode ? (
                <motion.div
                  key="forgot"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <h3 className="text-[22px] font-extrabold tracking-[-0.02em] text-slate-900 dark:text-white">
                    {t('auth.forgot.title')}
                  </h3>
                  <p className="mt-1 text-[13px] text-slate-500 dark:text-slate-400">
                    {t('auth.forgot.subtitle')}
                  </p>

                  {forgotSuccess ? (
                    <div className="mt-6 flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 p-4 dark:border-green-500/20 dark:bg-green-500/10">
                      <CheckCircle size={18} className="mt-0.5 shrink-0 text-green-600 dark:text-green-400" />
                      <div>
                        <p className="text-[13px] font-semibold text-green-700 dark:text-green-300">{forgotSuccess}</p>
                        {forgotResetUrl && (
                          <a href={forgotResetUrl} className="mt-1 inline-block text-[11.5px] text-green-700 underline dark:text-green-300">
                            {t('auth.forgot.resetCta')}
                          </a>
                        )}
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleForgotSubmit} className="mt-6 space-y-3">
                      <TextField
                        leftIcon={Mail}
                        type="email"
                        placeholder={t('auth.forgot.emailPlaceholder')}
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        required
                      />
                      {forgotError && <FieldError message={forgotError} />}
                      <Button type="submit" variant="primary" className="mt-1 w-full" disabled={forgotLoading}>
                        {forgotLoading ? t('auth.forgot.submitting') : t('auth.forgot.submit')}
                      </Button>
                    </form>
                  )}

                  <button
                    onClick={() => { setForgotMode(false); setForgotSuccess(''); setForgotError(''); setForgotResetUrl(''); setForgotEmail(''); }}
                    className="mt-6 text-[13px] font-semibold text-slate-500 transition-colors hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400"
                  >
                    {t('auth.forgot.back')}
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  key="login"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <h3 className="text-[22px] font-extrabold tracking-[-0.02em] text-slate-900 dark:text-white">
                    {t('auth.login.title')}
                  </h3>
                  <p className="mt-1 text-[13px] text-slate-500 dark:text-slate-400">
                    {t('auth.login.subtitle')}
                  </p>

                  <form onSubmit={handleSubmit} className="mt-6 space-y-3">
                    <TextField
                      leftIcon={Mail}
                      type="email"
                      placeholder={t('auth.login.emailPlaceholder')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      required
                    />

                    <TextField
                      leftIcon={Lock}
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('auth.login.passwordPlaceholder')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                      rightSlot={
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          className="text-slate-400 transition-colors hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                          aria-label={showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')}
                        >
                          {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                        </button>
                      }
                    />

                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => { setForgotMode(true); setForgotSuccess(''); setForgotError(''); setForgotResetUrl(''); }}
                        className="text-[12px] font-semibold text-slate-500 transition-colors hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400"
                      >
                        {t('auth.login.forgotPassword')}
                      </button>
                    </div>

                    {error && (
                      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
                        <FieldError message={error} />
                      </motion.div>
                    )}

                    <Button type="submit" variant="primary" className="mt-1 w-full" disabled={loading}>
                      {loading ? t('auth.login.submitting') : t('auth.login.submit')}
                    </Button>
                  </form>

                  {/* Social login */}
                  <div className="relative flex items-center py-5">
                    <div className="flex-grow border-t border-slate-200 dark:border-white/10" />
                    <span className="mx-3 shrink-0 text-[10.5px] font-extrabold uppercase tracking-[0.13em] text-slate-400 dark:text-slate-500">
                      {t('auth.login.orContinueWith')}
                    </span>
                    <div className="flex-grow border-t border-slate-200 dark:border-white/10" />
                  </div>

                  <SocialLoginButtons mode="login" />

                  <p className="mt-7 text-center text-[13px] text-slate-500 dark:text-slate-400">
                    {t('auth.login.noAccount')}{' '}
                    <Link
                      to="/register"
                      className="font-semibold text-blue-600 underline underline-offset-4 transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      {t('auth.login.signUpLink')}
                    </Link>
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
